import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';

interface AddTokensEditorProps {
  currentPurchasedBalance: number;
  disabled?: boolean;
  onAdd: (amount: number) => Promise<{ ok: boolean; message?: string }>;
}

export function AddTokensEditor({
  currentPurchasedBalance,
  disabled = false,
  onAdd,
}: AddTokensEditorProps) {
  const [amount, setAmount] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async() => {
    setInlineError(null);
    const trimmed = amount.trim();
    const parsedAmount = Number(trimmed);

    if (!Number.isInteger(parsedAmount) || parsedAmount <= 0) {
      setInlineError('Enter a whole number greater than zero.');
      return;
    }

    setIsSaving(true);
    const result = await onAdd(parsedAmount);
    setIsSaving(false);

    if (result.ok) {
      setAmount('');
      return;
    }

    setInlineError(result.message ?? 'Failed to add tokens.');
  };

  return (
    <div className="flex min-w-[180px] flex-col gap-1.5">
      <p className={cn(portalDs.bodySm, 'text-[#5E6278]')}>
        Current purchased tokens:
        {' '}
        <span className="font-mono font-semibold text-[#0D0F1A]">
          {currentPurchasedBalance}
        </span>
      </p>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min={1}
          step={1}
          value={amount}
          disabled={disabled || isSaving}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="Amount"
          aria-label="Tokens to add"
          className="h-8 w-[88px] rounded-lg border border-[#D9DBE3] px-2 text-[12px] text-[#0D0F1A] placeholder:text-[#9CA3AF] focus:border-[#0D0F1A] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="button"
          disabled={disabled || isSaving || !amount.trim()}
          onClick={() => void handleSubmit()}
          className="inline-flex h-8 items-center rounded-lg border border-[#D9DBE3] px-2 text-[12px] font-semibold text-[#0D0F1A] transition-colors hover:bg-[#F0F1F5] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2
              className="size-3.5 animate-spin"
              aria-hidden
            />
          ) : (
            'Add tokens'
          )}
        </button>
      </div>
      {inlineError ? (
        <p className={cn(portalDs.caption, 'text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : (
        <span className="text-[11px] text-[#9CA3AF]">
          Tops up purchased creative-generation tokens for this workspace.
          Granted tokens are not changed.
        </span>
      )}
    </div>
  );
}
